import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Button, 
  Input, 
  Select, 
  TextArea, 
  Message, 
  Header, 
  Icon, 
  Grid,
  Segment,
  Divider,
  Table,
  Label,
  Modal
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';

const typeOptions = [
  { key: 'suggestion', text: '功能建议', value: 1 },
  { key: 'minor', text: '小问题', value: 2 },
  { key: 'urgent', text: '紧急问题', value: 3 }
];

const FeedbackSubmit = () => {
  const [form, setForm] = useState({
    type: 1,
    title: '',
    description: '',
    execution_id: '',
    contact_info: '',
    device_info: ''
  });
  const [loading, setLoading] = useState(false);
  const [userFeedbacks, setUserFeedbacks] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [showMyFeedbacks, setShowMyFeedbacks] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // 自动检测设备信息
  useEffect(() => {
    const detectDeviceInfo = () => {
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      const language = navigator.language;
      
      let deviceInfo = `Platform: ${platform}, Language: ${language}`;
      
      // 检测操作系统
      if (userAgent.includes('Windows')) {
        deviceInfo += ', OS: Windows';
      } else if (userAgent.includes('Mac')) {
        deviceInfo += ', OS: macOS';
      } else if (userAgent.includes('Linux')) {
        deviceInfo += ', OS: Linux';
      } else if (userAgent.includes('Android')) {
        deviceInfo += ', OS: Android';
      } else if (userAgent.includes('iOS')) {
        deviceInfo += ', OS: iOS';
      }
      
      // 检测浏览器
      if (userAgent.includes('Chrome')) {
        deviceInfo += ', Browser: Chrome';
      } else if (userAgent.includes('Firefox')) {
        deviceInfo += ', Browser: Firefox';
      } else if (userAgent.includes('Safari')) {
        deviceInfo += ', Browser: Safari';
      } else if (userAgent.includes('Edge')) {
        deviceInfo += ', Browser: Edge';
      }
      
      setForm(prev => ({
        ...prev,
        device_info: deviceInfo
      }));
    };
    
    detectDeviceInfo();
  }, []);

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      showError('请输入反馈标题');
      return;
    }
    if (!form.description.trim()) {
      showError('请输入详细描述');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...form,
        execution_id: form.execution_id ? parseInt(form.execution_id) : null
      };

      const res = await API.post('/api/feedback/create', submitData);
      const { success, message } = res.data;
      
      if (success) {
        showSuccess('反馈提交成功！我们会尽快处理您的反馈。');
        // 重置表单
        setForm({
          type: 1,
          title: '',
          description: '',
          execution_id: '',
          contact_info: '',
          device_info: form.device_info // 保留设备信息
        });
        // 刷新用户反馈列表
        if (showMyFeedbacks) {
          loadUserFeedbacks();
        }
      } else {
        showError(message || '提交失败，请稍后重试');
      }
    } catch (error) {
      console.error('提交反馈失败:', error);
      showError('提交失败，请检查网络连接');
    }
    setLoading(false);
  };

  const loadUserFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const res = await API.get('/api/feedback/list');
      const { success, data } = res.data;
      if (success) {
        setUserFeedbacks(data.feedbacks || []);
      }
    } catch (error) {
      console.error('加载反馈记录失败:', error);
    }
    setLoadingFeedbacks(false);
  };

  const loadFeedbackStats = async () => {
    try {
      const res = await API.get('/api/feedback/stats');
      const { success, data } = res.data;
      if (success) {
        setFeedbackStats(data);
      }
    } catch (error) {
      console.error('加载统计信息失败:', error);
    }
  };

  const showFeedbackDetails = async (feedbackId) => {
    try {
      const res = await API.get(`/api/feedback/${feedbackId}`);
      const { success, data } = res.data;
      if (success) {
        setSelectedFeedback(data);
        setShowFeedbackModal(true);
      } else {
        showError('加载反馈详情失败');
      }
    } catch (error) {
      showError('加载反馈详情失败');
    }
  };

  const handleShowMyFeedbacks = () => {
    setShowMyFeedbacks(!showMyFeedbacks);
    if (!showMyFeedbacks) {
      loadUserFeedbacks();
      loadFeedbackStats();
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 1: return 'blue';    // 功能建议
      case 2: return 'orange';  // 小问题
      case 3: return 'red';     // 紧急问题
      default: return 'grey';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 1: return '功能建议';
      case 2: return '小问题';
      case 3: return '紧急问题';
      default: return '未知';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'grey';    // 待处理
      case 2: return 'yellow';  // 处理中
      case 3: return 'green';   // 已解决
      case 4: return 'blue';    // 已关闭
      default: return 'grey';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return '待处理';
      case 2: return '处理中';
      case 3: return '已解决';
      case 4: return '已关闭';
      default: return '未知';
    }
  };

  return (
    <div>
      <Header as="h2">
        <Icon name="comment" />
        <Header.Content>
          问题反馈
          <Header.Subheader>遇到问题或有改进建议？请告诉我们！</Header.Subheader>
        </Header.Content>
      </Header>

      <Grid stackable>
        <Grid.Row>
          <Grid.Column width={10}>
            <Card fluid>
              <Card.Header>
                <Icon name="edit" />
                提交反馈
              </Card.Header>
              <Card.Content>
                <Form>
                  <Form.Group widths='equal'>
                    <Form.Field required>
                      <label>反馈类型</label>
                      <Select
                        placeholder="选择反馈类型"
                        options={typeOptions}
                        value={form.type}
                        onChange={(e, { value }) => setForm({...form, type: value})}
                      />
                    </Form.Field>
                    <Form.Field>
                      <label>关联执行ID（可选）</label>
                      <Input
                        placeholder="如果与某次执行相关，请填写执行ID"
                        value={form.execution_id}
                        onChange={(e) => setForm({...form, execution_id: e.target.value})}
                      />
                    </Form.Field>
                  </Form.Group>

                  <Form.Field required>
                    <label>问题标题</label>
                    <Input
                      placeholder="请简要描述问题或建议..."
                      value={form.title}
                      onChange={(e) => setForm({...form, title: e.target.value})}
                    />
                  </Form.Field>

                  <Form.Field required>
                    <label>详细描述</label>
                    <TextArea
                      placeholder="请详细描述您遇到的问题或改进建议，包括：&#10;1. 具体的操作步骤&#10;2. 期望的结果和实际结果&#10;3. 错误信息（如有）&#10;4. 其他相关信息"
                      value={form.description}
                      onChange={(e) => setForm({...form, description: e.target.value})}
                      rows={6}
                    />
                  </Form.Field>

                  <Form.Field>
                    <label>联系方式（可选）</label>
                    <Input
                      placeholder="邮箱或电话，方便我们回复处理结果"
                      value={form.contact_info}
                      onChange={(e) => setForm({...form, contact_info: e.target.value})}
                    />
                  </Form.Field>

                  <Form.Field>
                    <label>设备信息（自动检测）</label>
                    <TextArea
                      placeholder="设备和环境信息"
                      value={form.device_info}
                      onChange={(e) => setForm({...form, device_info: e.target.value})}
                      rows={2}
                    />
                  </Form.Field>

                  <Divider />

                  <Button
                    primary
                    loading={loading}
                    onClick={handleSubmit}
                    size="large"
                  >
                    <Icon name="send" />
                    提交反馈
                  </Button>
                </Form>
              </Card.Content>
            </Card>
          </Grid.Column>

          <Grid.Column width={6}>
            <Segment>
              <Header as="h4">
                <Icon name="info circle" />
                反馈指南
              </Header>
              <Message info>
                <Message.Header>反馈类型说明</Message.Header>
                <Message.List>
                  <Message.Item><strong>功能建议：</strong>希望增加的新功能或改进现有功能</Message.Item>
                  <Message.Item><strong>小问题：</strong>不影响正常使用的小bug或不便之处</Message.Item>
                  <Message.Item><strong>紧急问题：</strong>严重影响使用的bug或故障</Message.Item>
                </Message.List>
              </Message>

              <Message warning>
                <Message.Header>提交前请检查</Message.Header>
                <Message.List>
                  <Message.Item>标题简洁明了</Message.Item>
                  <Message.Item>描述详细具体</Message.Item>
                  <Message.Item>包含复现步骤</Message.Item>
                  <Message.Item>提供错误截图（如有）</Message.Item>
                </Message.List>
              </Message>

              {feedbackStats && (
                <Segment>
                  <Header as="h5">我的反馈统计</Header>
                  <p>总反馈数：{feedbackStats.total}</p>
                  <p>待处理：{feedbackStats.pending}</p>
                  <p>处理中：{feedbackStats.processing}</p>
                  <p>已解决：{feedbackStats.resolved}</p>
                </Segment>
              )}

              <Button
                fluid
                basic
                color="blue"
                onClick={handleShowMyFeedbacks}
                loading={loadingFeedbacks}
              >
                <Icon name={showMyFeedbacks ? 'eye slash' : 'eye'} />
                {showMyFeedbacks ? '隐藏' : '查看'}我的反馈记录
              </Button>
            </Segment>
          </Grid.Column>
        </Grid.Row>

        {showMyFeedbacks && (
          <Grid.Row>
            <Grid.Column width={16}>
              <Card fluid>
                <Card.Header>
                  <Icon name="history" />
                  我的反馈记录
                </Card.Header>
                <Card.Content>
                  {userFeedbacks.length > 0 ? (
                    <Table celled>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>ID</Table.HeaderCell>
                          <Table.HeaderCell>标题/类型</Table.HeaderCell>
                          <Table.HeaderCell>状态</Table.HeaderCell>
                          <Table.HeaderCell>提交时间</Table.HeaderCell>
                          <Table.HeaderCell>操作</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {userFeedbacks.map((feedback) => (
                          <Table.Row key={feedback.id}>
                            <Table.Cell>{feedback.id}</Table.Cell>
                            <Table.Cell>
                              <div>
                                <strong>{feedback.title}</strong>
                                <br />
                                <Label color={getTypeColor(feedback.type)} size="small">
                                  {getTypeText(feedback.type)}
                                </Label>
                              </div>
                            </Table.Cell>
                            <Table.Cell>
                              <Label color={getStatusColor(feedback.status)} size="small">
                                {getStatusText(feedback.status)}
                              </Label>
                            </Table.Cell>
                            <Table.Cell>
                              {timestamp2string(feedback.created_at)}
                            </Table.Cell>
                            <Table.Cell>
                              <Button 
                                size="mini" 
                                icon="eye" 
                                onClick={() => showFeedbackDetails(feedback.id)}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  ) : (
                    <Message info>
                      <Icon name="inbox" />
                      您还没有提交过反馈。
                    </Message>
                  )}
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>

      {/* 反馈详情模态框 */}
      <Modal open={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} size="large">
        <Modal.Header>
          <Icon name="comment" />
          反馈详情 - ID: {selectedFeedback?.id}
        </Modal.Header>
        <Modal.Content>
          {selectedFeedback && (
            <div>
              <Grid stackable>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Segment>
                      <Header as="h4">基本信息</Header>
                      <p><strong>标题:</strong> {selectedFeedback.title}</p>
                      <p><strong>类型:</strong> 
                        <Label color={getTypeColor(selectedFeedback.type)} style={{ marginLeft: '10px' }}>
                          {getTypeText(selectedFeedback.type)}
                        </Label>
                      </p>
                      <p><strong>状态:</strong> 
                        <Label color={getStatusColor(selectedFeedback.status)} style={{ marginLeft: '10px' }}>
                          {getStatusText(selectedFeedback.status)}
                        </Label>
                      </p>
                      {selectedFeedback.execution_id && (
                        <p><strong>关联执行:</strong> #{selectedFeedback.execution_id}</p>
                      )}
                      <p><strong>提交时间:</strong> {timestamp2string(selectedFeedback.created_at)}</p>
                    </Segment>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Segment>
                      <Header as="h4">联系信息</Header>
                      <p><strong>联系方式:</strong> {selectedFeedback.contact_info || '未提供'}</p>
                      <p><strong>设备信息:</strong> {selectedFeedback.device_info || '未提供'}</p>
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
                
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Segment>
                      <Header as="h4">问题描述</Header>
                      <pre style={{ 
                        background: '#f4f4f4', 
                        padding: '10px', 
                        borderRadius: '4px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {selectedFeedback.description}
                      </pre>
                    </Segment>
                  </Grid.Column>
                </Grid.Row>
                
                {selectedFeedback.admin_response && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Segment color="green">
                        <Header as="h4" color="green">管理员回复</Header>
                        <pre style={{ 
                          background: '#f0f8f0', 
                          padding: '10px', 
                          borderRadius: '4px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {selectedFeedback.admin_response}
                        </pre>
                      </Segment>
                    </Grid.Column>
                  </Grid.Row>
                )}
              </Grid>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowFeedbackModal(false)}>
            <Icon name="close" />
            关闭
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default FeedbackSubmit;
